import { useState, memo, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  CalendarIcon, 
  ClockIcon, 
  CheckCircleIcon,
  PlusIcon,
  CalendarDaysIcon,
  CogIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  PhoneIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  TrashIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  SwatchIcon
} from '@heroicons/react/24/outline';
import NewAppointmentModal from './NewAppointmentModal';
import TodaysSchedulePanel from './TodaysSchedulePanel';
import PatientAppointmentSearch from './PatientAppointmentSearch';
import ConfirmDialog from '../records/ConfirmDialog';
import { 
  AppointmentStatus,
  migrateStatus,
  mapStatusToBackend
} from '../../types/appointment-status';

// Day mapping utilities
// Django: Monday=0, Tuesday=1, ..., Sunday=6
// FullCalendar: Sunday=0, Monday=1, ..., Saturday=6
const djangoToFullCalendarDay = (djangoDay: number): number => {
  // Convert Django day (Mon=0) to FullCalendar day (Sun=0)
  return djangoDay === 6 ? 0 : djangoDay + 1;
};

const fullCalendarToDjangoDay = (fcDay: number): number => {
  // Convert FullCalendar day (Sun=0) to Django day (Mon=0)
  return fcDay === 0 ? 6 : fcDay - 1;
};

// TypeScript interfaces
interface WorkingHours {
  id: number;
  day_of_week: number;
  is_working_day: boolean | string;
  has_split_shift: boolean | string;
  start_time?: string;
  end_time?: string;
  morning_start?: string;
  morning_end?: string;
  afternoon_start?: string;
  afternoon_end?: string;
}

interface BreakPeriod {
  id: number;
  day_of_week: number;
  name: string;
  start_time: string;
  end_time: string;
  break_type: string;
  description?: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string | Date;
  end: string | Date;
  backgroundColor: string;
  borderColor: string;
  textColor?: string;
  display?: string;
  classNames?: string[];
  extendedProps: {
    patient?: string;
    treatment?: string;
    provider?: string;
    status?: string;
    type?: string;
    breakType?: string;
    description?: string;
    notes?: string;
    phone?: string;
    appointmentId?: string;
    patientPhone?: string;
    patientEmail?: string;
    startTime?: string;
    endTime?: string;
    treatmentColor?: string;
    treatmentCategory?: string;
    treatmentBg?: string;
    treatmentBorder?: string;
    treatmentText?: string;
    statusBg?: string;
    statusBorder?: string;
    statusText?: string;
    patientMood?: string;
  };
}

/** Pure status color map — extracted outside component to avoid re-creation on every render */
const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  pending:      { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  confirmed:    { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  'checked-in': { bg: '#f3e8ff', border: '#8b5cf6', text: '#7c3aed' },
  'in-progress':{ bg: '#fed7aa', border: '#f97316', text: '#ea580c' },
  completed:    { bg: '#d1fae5', border: '#10b981', text: '#059669' },
  cancelled:    { bg: '#fee2e2', border: '#ef4444', text: '#dc2626' },
  'no-show':    { bg: '#fecaca', border: '#dc2626', text: '#991b1b' },
  rescheduled:  { bg: '#fde68a', border: '#d97706', text: '#92400e' },
};
const DEFAULT_STATUS_COLOR = { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280' };
const getStatusColors = (status: string) => STATUS_COLORS[status] ?? DEFAULT_STATUS_COLOR;

/** Patient mood emoji map */
const MOOD_EMOJIS: Record<string, { emoji: string; label: string; color: string }> = {
  neutral:   { emoji: '😐', label: 'Neutral', color: '#9ca3af' },
  happy:     { emoji: '😊', label: 'Happy', color: '#10b981' },
  anxious:   { emoji: '😰', label: 'Anxious', color: '#f59e0b' },
  angry:     { emoji: '😠', label: 'Angry', color: '#ef4444' },
  sad:       { emoji: '😢', label: 'Sad', color: '#3b82f6' },
  pain:      { emoji: '😣', label: 'Pain', color: '#f97316' },
  crying:    { emoji: '😭', label: 'Crying', color: '#8b5cf6' },
  bored:     { emoji: '😑', label: 'Bored', color: '#6b7280' },
  suffering: { emoji: '🤕', label: 'Suffering', color: '#dc2626' },
};

/** Color mode types */
type CalendarColorMode = 'status' | 'treatment';

/** Day category assignment interface */
interface DayCategoryAssignment {
  id?: string;
  day_of_week: number;
  category_name: string;
  color: string;
  is_active: boolean;
}

/** Helper to generate treatment type colors with proper contrast */
const getTreatmentColors = (color: string) => {
  // Convert hex to RGB for generating lighter/darker variants
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return {
    bg: `rgba(${r}, ${g}, ${b}, 0.15)`,
    border: color,
    text: `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`,
  };
};

function AppointmentsClientWithSettings() {
  const { t } = useTranslation(['appointments', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const initialPatientFromRoute = (location.state as any)?.selectPatient || null;
  const [selectedView, setSelectedView] = useState('week');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [calendarRef, setCalendarRef] = useState<FullCalendar | null>(null);

  // Themed dialog state (replaces native alert/confirm)
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'danger' | 'warning' | 'info';
    confirmLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({ open: false, title: '', message: '', variant: 'info' });

  const [toastState, setToastState] = useState<{
    show: boolean;
    message: string;
    type: 'error' | 'success' | 'info';
  }>({ show: false, message: '', type: 'info' });

  const showToast = (message: string, type: 'error' | 'success' | 'info' = 'error') => {
    setToastState({ show: true, message, type });
    setTimeout(() => setToastState(prev => ({ ...prev, show: false })), 4000);
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    variant: 'danger' | 'warning' | 'info' = 'warning'
  ) => {
    setDialogState({
      open: true,
      title,
      message,
      variant,
      confirmLabel: undefined,
      onConfirm: () => {
        setDialogState(prev => ({ ...prev, open: false }));
        onConfirm();
      },
      onCancel: () => setDialogState(prev => ({ ...prev, open: false }))
    });
  };
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedDuration, setSelectedDuration] = useState<number>(30); // Default 30 minutes
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  
  // Patient search state
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; firstName: string; lastName: string } | null>(null);
  const [selectedPatientAppointments, setSelectedPatientAppointments] = useState<any[]>([]);
  const [currentPatientAppointmentIndex, setCurrentPatientAppointmentIndex] = useState(0);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null); // Track which specific appointment to highlight

  
  // Appointment settings state
  const [workingHours, setWorkingHours] = useState<WorkingHours[]>([]);
  const [breakPeriods, setBreakPeriods] = useState<BreakPeriod[]>([]);
  const [appointmentSettings, setAppointmentSettings] = useState<{
    enable_group_sessions: boolean;
    allow_overlapping_appointments: boolean;
    max_patients_per_group: number;
    time_slot_interval: number; // Duration in minutes for calendar time slots
  }>({
    enable_group_sessions: false,
    allow_overlapping_appointments: false,
    max_patients_per_group: 1,
    time_slot_interval: 15, // Default 15-minute slots
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [appointments, setAppointments] = useState<CalendarEvent[]>([]);
  const [language, setLanguage] = useState<string>('en');

  // Calendar color mode state
  const [calendarColorMode, setCalendarColorMode] = useState<CalendarColorMode>('status');
  const [hideNonWorkingHours, setHideNonWorkingHours] = useState(false);
  const [dayCategories, setDayCategories] = useState<DayCategoryAssignment[]>([]);
  const [showDayCategoryPanel, setShowDayCategoryPanel] = useState(false);
  // showMoodPicker tracks which appointment's mood picker is open (null = none)
  const [_showMoodPicker, setShowMoodPicker] = useState<string | null>(null);

  // Hover tooltip state
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, transform: 'translateX(-50%) translateY(-100%)' });
  const [showTooltip, setShowTooltip] = useState(false);
  const [hideTooltipTimeout, setHideTooltipTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showTooltipTimeout, setShowTooltipTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Selection state for sticky tooltips
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [isTooltipSelected, setIsTooltipSelected] = useState(false);

  // Schedule panel date navigation
  const [schedulePanelDate, setSchedulePanelDate] = useState<Date>(new Date());

  // Load appointment settings
  useEffect(() => {
    loadAppointmentSettings();
    loadAppointments();
    loadDayCategories();
  }, []);

  // Note: We now highlight selected patient appointments instead of filtering them

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      clearAllTooltipTimeouts();
    };
  }, [hideTooltipTimeout, showTooltipTimeout]);

  // Handle clicks outside appointment/tooltip to deselect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isTooltipSelected) return;
      
      const target = event.target as Element;
      
      // Check if click is inside tooltip
      const tooltipElement = document.querySelector('.appointment-tooltip');
      if (tooltipElement?.contains(target)) return;
      
      // Check if click is on a FullCalendar appointment
      const isAppointmentClick = target.closest('.fc-event');
      if (isAppointmentClick) return; // Let eventClick handler deal with it
      
      // Click is outside both tooltip and appointments - deselect
      deselectAppointment();
    };
    
    if (isTooltipSelected) {
      document.addEventListener('click', handleClickOutside, true);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isTooltipSelected]);

  const loadAppointments = async (): Promise<void> => {
    try {
      const response = await fetch('/api/appointments/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        const appointmentEvents = data.appointments?.map((appointment: any) => {
          // Migrate legacy status to new professional status
          const migratedStatus = migrateStatus(appointment.status || 'pending');
          
          const colors = getStatusColors(migratedStatus);
          const treatmentColor = appointment.treatment_type?.color || '#3b82f6';
          const treatmentColors = getTreatmentColors(treatmentColor);
          
          return {
            id: appointment.id,
            title: `${appointment.patient?.name || 'No Patient'} - ${appointment.treatment_type?.name || 'No Treatment'}`,
            start: appointment.start_time,
            end: appointment.end_time,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            textColor: colors.text,
            extendedProps: {
              patient: appointment.patient?.name,
              patientPhone: appointment.patient?.phone || appointment.patient?.phone_number,
              patientEmail: appointment.patient?.email,
              treatment: appointment.treatment_type?.name,
              treatmentColor: treatmentColor,
              treatmentCategory: appointment.treatment_type?.category,
              treatmentBg: treatmentColors.bg,
              treatmentBorder: treatmentColors.border,
              treatmentText: treatmentColors.text,
              provider: appointment.dentist?.name,
              status: migratedStatus,
              statusBg: colors.bg,
              statusBorder: colors.border,
              statusText: colors.text,
              notes: appointment.notes,
              appointmentId: appointment.id,
              startTime: appointment.start_time,
              endTime: appointment.end_time,
              patientMood: appointment.patient_mood || 'neutral',
              type: 'appointment'
            }
          };
        }) || [];
        setAppointments(appointmentEvents);
      }
    } catch (error) {
      console.error('Failed to load appointments:', error);
    }
  };

  const loadAppointmentSettings = async (): Promise<void> => {
    try {
      // Load working hours
      const workingHoursResponse = await fetch('/api/appointments/working-hours/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (workingHoursResponse.ok) {
        const workingHoursData = await workingHoursResponse.json();
        setWorkingHours(workingHoursData.working_hours || []);
        setLanguage(workingHoursData.language || 'en');
      }

      // Load break periods
      const breakPeriodsResponse = await fetch('/api/appointments/break-periods/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (breakPeriodsResponse.ok) {
        const breakPeriodsData = await breakPeriodsResponse.json();
        setBreakPeriods(breakPeriodsData.break_periods || []);
      }

      // Load appointment settings (group sessions, overlapping, etc.)
      const settingsResponse = await fetch('/api/appointments/settings/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        
        // Extract settings from the nested structure
        const settings = settingsData.settings || settingsData;
        
        setAppointmentSettings({
          enable_group_sessions: settings.enable_group_sessions || false,
          allow_overlapping_appointments: settings.allow_overlapping_appointments || false,
          max_patients_per_group: settings.max_patients_per_group || 1,
          time_slot_interval: settings.time_slot_interval || 15,
        });
        
        // Load calendar display settings
        if (settings.calendar_color_mode) {
          setCalendarColorMode(settings.calendar_color_mode as CalendarColorMode);
        }
        if (settings.hide_non_working_hours !== undefined) {
          setHideNonWorkingHours(settings.hide_non_working_hours);
        }
      } else {
        console.error('Failed to load appointment settings:', settingsResponse.statusText);
      }

      setSettingsLoaded(true);
    } catch (error) {
      console.error('Failed to load appointment settings:', error);
      setSettingsLoaded(true); // Still mark as loaded to prevent infinite loading
    }
  };

  const handleCreateAppointment = async (appointmentData: any): Promise<any> => {
    try {
      // If editing, use PUT to update
      if (editingAppointmentId) {
        const response = await fetch(`/api/appointments/${editingAppointmentId}/update/`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(appointmentData)
        });

        const data = await response.json();

        if (response.ok) {
          await loadAppointments();
          setEditingAppointmentId(null);
          return data;
        } else {
          throw new Error(data.error || 'Failed to update appointment');
        }
      }

      // Otherwise create new
      const response = await fetch('/api/appointments/create/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(appointmentData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Reload appointments to refresh the calendar
        await loadAppointments();
        return data; // Return the response data
      } else {
        throw new Error(data.error || 'Failed to create appointment');
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      throw error;
    }
  };

  // Handler for appointment status updates
  const handleAppointmentStatusUpdate = async (appointmentId: string, newStatus: AppointmentStatus): Promise<void> => {
    try {
      const response = await fetch(`/api/appointments/${appointmentId}/update/`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          status: mapStatusToBackend(newStatus)
        })
      });

      if (response.ok) {
        // Reload appointments to refresh both calendar and today's schedule
        await loadAppointments();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update appointment status');
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }
  };

  // Load day category assignments
  const loadDayCategories = async (): Promise<void> => {
    try {
      const response = await fetch('/api/appointment-day-categories/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setDayCategories(data.day_categories || []);
      }
    } catch (error) {
      console.error('Failed to load day categories:', error);
    }
  };

  // Save day category assignment
  const saveDayCategory = async (assignment: DayCategoryAssignment): Promise<void> => {
    try {
      await fetch('/api/appointment-day-categories/update/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ assignments: [assignment] })
      });
      await loadDayCategories();
    } catch (error) {
      console.error('Failed to save day category:', error);
    }
  };

  // Toggle calendar color mode and persist to settings
  const toggleColorMode = async () => {
    const newMode: CalendarColorMode = calendarColorMode === 'status' ? 'treatment' : 'status';
    setCalendarColorMode(newMode);
    try {
      await fetch('/api/appointments/settings/update/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ calendar_color_mode: newMode })
      });
    } catch (error) {
      console.error('Failed to save color mode:', error);
    }
  };

  // Toggle hide non-working hours and persist
  const toggleHideNonWorkingHours = async () => {
    const newVal = !hideNonWorkingHours;
    setHideNonWorkingHours(newVal);
    try {
      await fetch('/api/appointments/settings/update/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ hide_non_working_hours: newVal })
      });
    } catch (error) {
      console.error('Failed to save hide non-working hours:', error);
    }
  };

  // Update patient mood for an appointment
  const handleMoodUpdate = async (appointmentId: string, mood: string): Promise<void> => {
    try {
      const response = await fetch(`/api/appointments/${appointmentId}/mood/`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ patient_mood: mood })
      });
      if (response.ok) {
        await loadAppointments();
        setShowMoodPicker(null);
      }
    } catch (error) {
      console.error('Failed to update mood:', error);
    }
  };

  // Tooltip helper functions
  const clearTooltipTimeout = () => {
    if (hideTooltipTimeout) {
      clearTimeout(hideTooltipTimeout);
      setHideTooltipTimeout(null);
    }
  };

  const clearShowTooltipTimeout = () => {
    if (showTooltipTimeout) {
      clearTimeout(showTooltipTimeout);
      setShowTooltipTimeout(null);
    }
  };

  const clearAllTooltipTimeouts = () => {
    clearTooltipTimeout();
    clearShowTooltipTimeout();
  };

  const scheduleTooltipHide = () => {
    // Don't hide tooltip if it's selected
    if (isTooltipSelected) {
      return;
    }
    
    clearAllTooltipTimeouts();
    
    const timeout = setTimeout(() => {
      setShowTooltip(false);
      setHoveredEvent(null);
    }, 300); // 300ms delay to allow mouse movement to tooltip
    setHideTooltipTimeout(timeout);
  };

  const scheduleTooltipShow = (eventData: any, position: { x: number; y: number; transform: string }) => {
    // Don't show hover tooltip if another appointment is selected
    if (isTooltipSelected && selectedEvent && selectedEvent.id !== eventData.id) {
      return;
    }
    
    clearAllTooltipTimeouts();
    
    // Validate position data
    if (isNaN(position.x) || isNaN(position.y)) {
      console.error('❌ Invalid position data:', position);
      return;
    }
    
    // Set event data immediately
    setHoveredEvent(eventData);
    setTooltipPosition(position);
    
    // Show tooltip after a short delay to prevent flickering
    const timeout = setTimeout(() => {
      setShowTooltip(true);
    }, 150); // Short delay to prevent rapid show/hide
    setShowTooltipTimeout(timeout);
  };

  const hideTooltipImmediately = () => {
    clearAllTooltipTimeouts();
    setShowTooltip(false);
    setHoveredEvent(null);
  };

  const keepTooltipVisible = () => {
    clearAllTooltipTimeouts();
    // Keep tooltip visible without scheduling hide
  };

  // Selection functions for sticky tooltips
  const selectAppointment = (eventData: any, position: { x: number; y: number; transform: string }) => {
    // Clear any existing timeouts
    clearAllTooltipTimeouts();
    
    // Set selection state
    setSelectedEvent(eventData);
    setHoveredEvent(eventData);
    setTooltipPosition(position);
    setIsTooltipSelected(true);
    setShowTooltip(true);
  };

  const deselectAppointment = () => {
    clearAllTooltipTimeouts();
    setSelectedEvent(null);
    setHoveredEvent(null);
    setIsTooltipSelected(false);
    setShowTooltip(false);
  };

  const isAppointmentSelected = (appointmentId: string) => {
    return selectedEvent?.id === appointmentId;
  };

  // Handler for deleting appointment
  const handleDeleteAppointment = async (appointmentId: string): Promise<void> => {
    showConfirmDialog(
      t('deleteAppointment', 'Delete Appointment'),
      t('confirmDeleteAppointment', 'Are you sure you want to delete this appointment?'),
      async () => {
        try {
          const response = await fetch(`/api/appointments/${appointmentId}/delete/`, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            await loadAppointments();
            hideTooltipImmediately();
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to delete appointment');
          }
        } catch (error) {
          console.error('Error deleting appointment:', error);
          showToast(`Failed to delete appointment: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
      },
      'danger'
    );
  };

  // Handler for editing appointment
  const handleEditAppointment = (appointmentId: string): void => {
    hideTooltipImmediately();
    
    // Find the appointment in the calendar events
    const event = appointments.find(a => String(a.id) === appointmentId || a.extendedProps?.appointmentId === appointmentId);
    if (!event) return;
    
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationMin = Math.round(durationMs / 60000);
    
    // Pre-fill the modal with existing appointment data
    setEditingAppointmentId(appointmentId);
    setSelectedDate(startDate.toISOString().split('T')[0]);
    setSelectedTime(startDate.toTimeString().slice(0, 5));
    setSelectedDuration(durationMin || 30);
    
    // Set patient if available
    const patientName = event.extendedProps?.patient || '';
    if (patientName) {
      const parts = patientName.split(' ');
      setSelectedPatient({
        id: '', // Patient ID not stored in calendar event — modal will match by name
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || ''
      });
    }
    
    setShowNewAppointmentModal(true);
  };

  // Handler for quick status update from tooltip
  const handleQuickStatusUpdate = async (appointmentId: string, newStatus: AppointmentStatus): Promise<void> => {
    try {
      await handleAppointmentStatusUpdate(appointmentId, newStatus);
      hideTooltipImmediately(); // Close tooltip
    } catch (error) {
      console.error('Error updating status:', error);
      showToast(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  const handleNewAppointmentClick = () => {
    setSelectedDate('');
    setSelectedTime('');
    setSelectedDuration(30); // Default duration
    setShowNewAppointmentModal(true);
  };

  // Patient search handlers
  const handlePatientSelect = (patientName: string, appointments?: any[]) => {
    if (!patientName || patientName.trim() === '') {
      // Clear all patient filtering
      setSelectedPatientAppointments([]);
      setCurrentPatientAppointmentIndex(0);
      setSelectedAppointmentId(null); // Clear selected appointment
    } else {
      // Set patient filter - but DO NOT highlight any appointment yet
      setSelectedAppointmentId(null); // Clear any previous appointment selection
      if (appointments) {
        setSelectedPatientAppointments(appointments);
        setCurrentPatientAppointmentIndex(0);
      }
    }
  };

  const handleAppointmentNavigate = (appointment: any) => {
    // Set the selected appointment ID for highlighting ONLY this appointment
    setSelectedAppointmentId(appointment.id);
    
    // Navigate calendar to the appointment date
    if (calendarRef && (appointment.start_time || appointment.appointment_date)) {
      const calendarApi = calendarRef.getApi();
      const appointmentDate = appointment.start_time ? 
        new Date(appointment.start_time) : 
        new Date(appointment.appointment_date);
      
      calendarApi.gotoDate(appointmentDate);
      
      // Change to day view for better focus
      if (selectedView !== 'day') {
        calendarApi.changeView('timeGridDay');
        setSelectedView('day');
      }
      
      // Update the current appointment index if this is from patient appointments
      if (selectedPatientAppointments.length > 0) {
        const appointmentIndex = selectedPatientAppointments.findIndex(apt => 
          apt.id === appointment.id || 
          apt.appointment_date === appointment.appointment_date
        );
        if (appointmentIndex >= 0) {
          setCurrentPatientAppointmentIndex(appointmentIndex);
        }
      }
    }
  };

  // Custom navigation handlers for patient appointments
  const handlePatientAppointmentNavigation = (direction: 'prev' | 'next') => {
    if (selectedPatientAppointments.length === 0) {
      // No patient selected, use default calendar navigation
      if (calendarRef) {
        const calendarApi = calendarRef.getApi();
        if (direction === 'prev') {
          calendarApi.prev();
        } else {
          calendarApi.next();
        }
      }
      return;
    }

    // Navigate between patient appointments
    let newIndex = currentPatientAppointmentIndex;
    if (direction === 'prev') {
      newIndex = Math.min(currentPatientAppointmentIndex + 1, selectedPatientAppointments.length - 1);
    } else {
      newIndex = Math.max(currentPatientAppointmentIndex - 1, 0);
    }

    if (newIndex !== currentPatientAppointmentIndex) {
      setCurrentPatientAppointmentIndex(newIndex);
      const appointment = selectedPatientAppointments[newIndex];
      
      if (calendarRef && appointment.appointment_date) {
        const calendarApi = calendarRef.getApi();
        calendarApi.gotoDate(new Date(appointment.appointment_date));
      }
    }
  };



  // Helper function to validate if a date/time is within working hours
  const isWithinWorkingHours = (date: Date): { isValid: boolean; reason?: string } => {
    const fcDayOfWeek = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const djangoDayOfWeek = fullCalendarToDjangoDay(fcDayOfWeek);
    
    const dayWorkingHours = workingHours.find(h => h.day_of_week === djangoDayOfWeek);
    
    if (!dayWorkingHours || !dayWorkingHours.is_working_day) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return {
        isValid: false,
        reason: `${dayNames[fcDayOfWeek]} is not configured as a working day.`
      };
    }

    // Check if time falls within working hours
    const clickTime = date.toTimeString().slice(0, 8); // HH:MM:SS format
    
    let timeValid = false;
    let workingHoursText = '';
    
    if (dayWorkingHours.has_split_shift === true || dayWorkingHours.has_split_shift === 'true') {
      // Check split shift hours
      const morningValid = Boolean(
        dayWorkingHours.morning_start && dayWorkingHours.morning_end &&
        clickTime >= dayWorkingHours.morning_start && 
        clickTime < dayWorkingHours.morning_end // Use < for end time to be more precise
      );
      const afternoonValid = Boolean(
        dayWorkingHours.afternoon_start && dayWorkingHours.afternoon_end &&
        clickTime >= dayWorkingHours.afternoon_start && 
        clickTime < dayWorkingHours.afternoon_end
      );
      timeValid = morningValid || afternoonValid;
      workingHoursText = `${dayWorkingHours.morning_start}-${dayWorkingHours.morning_end} and ${dayWorkingHours.afternoon_start}-${dayWorkingHours.afternoon_end}`;
    } else {
      // Check single shift hours
      timeValid = Boolean(
        dayWorkingHours.start_time && dayWorkingHours.end_time &&
        clickTime >= dayWorkingHours.start_time && 
        clickTime < dayWorkingHours.end_time
      );
      workingHoursText = `${dayWorkingHours.start_time}-${dayWorkingHours.end_time}`;
    }
    
    if (!timeValid) {
      return {
        isValid: false,
        reason: `This time is outside working hours. Working hours are: ${workingHoursText}`
      };
    }
    
    return { isValid: true };
  };

  const handleDateClick = (info: any) => {
    // Get the clicked date and time
    const clickedDate = info.date; // Use the Date object instead of dateStr
    
    // Validate working hours first
    const workingHoursValidation = isWithinWorkingHours(clickedDate);
    if (!workingHoursValidation.isValid) {
      showToast(`Cannot create appointment: ${workingHoursValidation.reason}`, 'error');
      return;
    }
    
    const formattedDate = clickedDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    const formattedTime = clickedDate.toTimeString().slice(0, 5); // Format as HH:MM
    
    setSelectedDate(formattedDate);
    setSelectedTime(formattedTime);
    setSelectedDuration(30); // Default duration for single click
    setShowNewAppointmentModal(true);
  };

  const handleDateSelect = (info: any) => {
    // Handle multi-slot selection (when user drags across multiple time slots)
    const startDate = info.start; // Start of selection
    const endDate = info.end;     // End of selection
    
    // Validate working hours for the start time
    const workingHoursValidation = isWithinWorkingHours(startDate);
    if (!workingHoursValidation.isValid) {
      showToast(`Cannot create appointment: ${workingHoursValidation.reason}`, 'error');
      return;
    }
    
    const formattedDate = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    const formattedTime = startDate.toTimeString().slice(0, 5); // Format as HH:MM
    
    // Calculate duration in minutes
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationMinutes = Math.round(durationMs / (1000 * 60));
    
    // Store the selection info for the appointment modal
    setSelectedDate(formattedDate);
    setSelectedTime(formattedTime);
    setSelectedDuration(durationMinutes);
    setShowNewAppointmentModal(true);
    
    // Clear the selection after opening the modal
    if (calendarRef) {
      const calendarApi = calendarRef.getApi();
      calendarApi.unselect();
    }
  };

  // Get dynamic time slot configuration based on settings
  const getTimeSlotConfig = () => {
    const slotDurationMinutes = appointmentSettings.time_slot_interval || 15;
    
    // Format duration as HH:MM:SS for FullCalendar
    const formatDuration = (minutes: number) => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    };
    
    // Always show labels only on the hour (continuous lines)
    // Slot lines (between hours) will be styled as dotted via CSS
    const labelIntervalMinutes = 60; // Always 1 hour for labels
    
    return {
      slotDuration: formatDuration(slotDurationMinutes),
      slotLabelInterval: formatDuration(labelIntervalMinutes),
      snapDuration: formatDuration(slotDurationMinutes),
      minDuration: slotDurationMinutes, // For validation
      slotsPerHour: Math.floor(60 / slotDurationMinutes)
    };
  };

  // Convert working hours to FullCalendar businessHours format
  const getBusinessHours = () => {
    if (!settingsLoaded || workingHours.length === 0) {
      return undefined;
    }

    return workingHours
      .filter(hours => hours.is_working_day)
      .map(hours => {
        // Convert Django day to FullCalendar day
        const fcDayOfWeek = djangoToFullCalendarDay(hours.day_of_week);
        
        if (hours.has_split_shift === true || hours.has_split_shift === 'true') {
          // Split shift - create two business hour periods
          return [
            {
              daysOfWeek: [fcDayOfWeek],
              startTime: hours.morning_start,
              endTime: hours.morning_end,
            },
            {
              daysOfWeek: [fcDayOfWeek],
              startTime: hours.afternoon_start,
              endTime: hours.afternoon_end,
            }
          ];
        } else {
          // Single shift
          return {
            daysOfWeek: [fcDayOfWeek],
            startTime: hours.start_time,
            endTime: hours.end_time,
          };
        }
      })
      .flat(); // Flatten in case of split shifts
  };

  // Get calendar time bounds
  const getCalendarTimeBounds = () => {
    if (!settingsLoaded || workingHours.length === 0) {
      return { minTime: '08:00:00', maxTime: '18:00:00' };
    }

    const workingDays = workingHours.filter(h => h.is_working_day);
    if (workingDays.length === 0) {
      return { minTime: '08:00:00', maxTime: '18:00:00' };
    }

    let earliestStart = '23:59';
    let latestEnd = '00:00';

    workingDays.forEach(hours => {
      if (hours.has_split_shift) {
        if (hours.morning_start && hours.morning_start < earliestStart) {
          earliestStart = hours.morning_start;
        }
        if (hours.afternoon_end && hours.afternoon_end > latestEnd) {
          latestEnd = hours.afternoon_end;
        }
      } else {
        if (hours.start_time && hours.start_time < earliestStart) {
          earliestStart = hours.start_time;
        }
        if (hours.end_time && hours.end_time > latestEnd) {
          latestEnd = hours.end_time;
        }
      }
    });

    if (hideNonWorkingHours) {
      // When hiding non-working hours, use exact working hour bounds (no padding)
      const startHour = parseInt(earliestStart.split(':')[0]);
      const latestEndParts = latestEnd.split(':');
      const latestEndHour = parseInt(latestEndParts[0]);
      const latestEndMinute = parseInt(latestEndParts[1] || '0');
      const endHour = latestEndMinute > 0 ? latestEndHour + 1 : latestEndHour;
      
      return {
        minTime: `${startHour.toString().padStart(2, '0')}:00:00`,
        maxTime: `${Math.min(24, endHour).toString().padStart(2, '0')}:00:00`
      };
    }

    // Add some padding - start 1 hour earlier, end 1 hour later
    const startHour = Math.max(0, parseInt(earliestStart.split(':')[0]) - 1);
    
    // Handle end time properly
    const latestEndParts = latestEnd.split(':');
    const latestEndHour = parseInt(latestEndParts[0]);
    const latestEndMinute = parseInt(latestEndParts[1] || '0');
    
    let endHour;
    if (latestEndHour >= 23 || (latestEndHour >= 22 && latestEndMinute > 0)) {
      endHour = 24;
    } else {
      endHour = Math.min(24, latestEndHour + 1);
    }

    return {
      minTime: `${startHour.toString().padStart(2, '0')}:00:00`,
      maxTime: `${endHour.toString().padStart(2, '0')}:00:00`
    };
  };

  // Convert break periods to FullCalendar events
  const getBreakEvents = (): CalendarEvent[] => {
    if (!settingsLoaded || breakPeriods.length === 0) {
      return [];
    }

    const breakEvents: CalendarEvent[] = [];
    
    breakPeriods.forEach(breakPeriod => {
      // Create a recurring break event for the specific day
      const dayOfWeek = breakPeriod.day_of_week;
      
      // Create break events for the next few weeks
      for (let weekOffset = 0; weekOffset < 4; weekOffset++) {
        const date = new Date();
        const currentDay = date.getDay();
        const daysUntilTarget = (dayOfWeek - currentDay + 7) % 7;
        date.setDate(date.getDate() + daysUntilTarget + (weekOffset * 7));
        
        const startDateTime = new Date(date);
        const endDateTime = new Date(date);
        
        const [startHour, startMinute] = breakPeriod.start_time.split(':');
        const [endHour, endMinute] = breakPeriod.end_time.split(':');
        
        startDateTime.setHours(parseInt(startHour), parseInt(startMinute), 0);
        endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0);
        
        breakEvents.push({
          id: `break-${breakPeriod.id}-${weekOffset}`,
          title: `🚫 ${breakPeriod.name}`,
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
          display: 'background',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'rgba(239, 68, 68, 0.3)',
          classNames: ['break-period'],
          extendedProps: {
            type: 'break',
            breakType: breakPeriod.break_type,
            description: breakPeriod.description
          }
        });
      }
    });
    
    return breakEvents;
  };

  // Update calendar view when selectedView changes
  useEffect(() => {
    if (calendarRef) {
      const calendarApi = calendarRef.getApi();
      const viewName = selectedView === 'day' ? 'timeGridDay' : 
                      selectedView === 'week' ? 'timeGridWeek' : 
                      'dayGridMonth';
      calendarApi.changeView(viewName);
    }
  }, [selectedView, calendarRef]);

  // Listen to window resize events to update FullCalendar dimensions
  // This handles sidebar collapse/expand and any other layout changes
  useEffect(() => {
    if (!calendarRef) return;

    const handleResize = () => {
      const calendarApi = calendarRef.getApi();
      calendarApi.updateSize();
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calendarRef]);

  // Update FullCalendar dimensions when internal calendar expand/collapse changes
  useEffect(() => {
    if (calendarRef) {
      // Wait for CSS transition to complete (300ms transition-all duration-300)
      const timeoutId = setTimeout(() => {
        const calendarApi = calendarRef.getApi();
        calendarApi.updateSize();
      }, 350);

      return () => clearTimeout(timeoutId);
    }
  }, [isCalendarExpanded, calendarRef]);

  // Filter today's appointments from real data
  const getSchedulePanelAppointments = () => {
    const targetDate = schedulePanelDate;
    const targetStr = targetDate.toDateString();
    
    return appointments
      .filter(event => {
        // Filter for appointments only (not breaks)
        if (event.extendedProps?.type !== 'appointment') return false;
        
        // Check if the appointment matches the selected date
        const appointmentDate = new Date(event.start);
        return appointmentDate.toDateString() === targetStr;
      })
      .map(event => {
        const startTime = new Date(event.start);
        const endTime = new Date(event.end);
        const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        
        return {
          id: String(event.id), // Keep ID as string to match Appointment interface
          time: startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          patient: event.extendedProps?.patient || 'Unknown Patient',
          treatment: event.extendedProps?.treatment || 'No Treatment',
          provider: event.extendedProps?.provider || 'No Provider',
          status: migrateStatus(event.extendedProps?.status || 'pending'),
          duration: `${duration} min`,
          start_time: event.start as string,
          notes: event.extendedProps?.notes,
          phone: event.extendedProps?.phone,
          patientMood: event.extendedProps?.patientMood || 'neutral',
        };
      })
      .sort((a, b) => {
        // Sort by appointment time
        const timeA = new Date(a.start_time!).getTime();
        const timeB = new Date(b.start_time!).getTime();
        return timeA - timeB;
      });
  };

  const schedulePanelAppointments = useMemo(() => getSchedulePanelAppointments(), [appointments, schedulePanelDate]);

  const timeBounds = useMemo(() => getCalendarTimeBounds(), [workingHours, settingsLoaded, hideNonWorkingHours]);
  const businessHours = useMemo(() => getBusinessHours(), [workingHours, settingsLoaded]);

  // Compute hidden days (non-working days) for FullCalendar
  const hiddenDays = useMemo(() => {
    if (!hideNonWorkingHours || !settingsLoaded || workingHours.length === 0) return [];
    return workingHours
      .filter(h => !h.is_working_day)
      .map(h => djangoToFullCalendarDay(h.day_of_week));
  }, [workingHours, settingsLoaded, hideNonWorkingHours]);

  // Always show all appointments, but highlight ONLY the specific selected appointment
  // Apply color mode: status colors vs treatment type colors
  const highlightedAppointments = useMemo(() => appointments.map(appointment => {
    // Only highlight the appointment that was clicked via "Navigate to Appointment" button
    const isThisAppointmentSelected = selectedAppointmentId && 
      (appointment.id === selectedAppointmentId || 
       appointment.extendedProps?.appointmentId === selectedAppointmentId);
    
    // Determine colors based on color mode
    let bgColor = appointment.backgroundColor;
    let borderColorVal = appointment.borderColor;
    let textColorVal = appointment.textColor;
    
    if (calendarColorMode === 'treatment' && appointment.extendedProps?.treatmentColor) {
      bgColor = appointment.extendedProps.treatmentBg || bgColor;
      borderColorVal = appointment.extendedProps.treatmentBorder || borderColorVal;
      textColorVal = appointment.extendedProps.treatmentText || textColorVal;
    }
    
    return {
      ...appointment,
      backgroundColor: isThisAppointmentSelected 
        ? (bgColor || '#3b82f6') + '15' 
        : bgColor,
      borderColor: isThisAppointmentSelected ? '#ef4444' : borderColorVal,
      textColor: textColorVal,
      // Add professional highlighting for the SPECIFIC selected appointment only
      className: isThisAppointmentSelected ? 'selected-patient-appointment' : '',
      borderWidth: isThisAppointmentSelected ? '3px' : undefined,
    };
  }), [appointments, selectedAppointmentId, calendarColorMode]);
  
  const breakEvents = useMemo(() => getBreakEvents(), [breakPeriods, workingHours, settingsLoaded]);
  const allEvents = useMemo(() => [...highlightedAppointments, ...breakEvents], [highlightedAppointments, breakEvents]);
  const timeSlotConfig = useMemo(() => getTimeSlotConfig(), [appointmentSettings.time_slot_interval]);

  // Compute real statistics from appointment data
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + (language === 'fr' ? 1 : 0));
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    let todayCount = 0;
    let weekCount = 0;
    let confirmedCount = 0;
    let pendingCount = 0;

    for (const apt of appointments) {
      const start = apt.start ? new Date(apt.start as string) : null;
      if (!start) continue;
      const status = apt.extendedProps?.status || '';
      if (start >= today && start <= endOfToday) todayCount++;
      if (start >= startOfWeek && start <= endOfWeek) weekCount++;
      if (status === 'confirmed') confirmedCount++;
      if (status === 'pending') pendingCount++;
    }
    return { todayCount, weekCount, confirmedCount, pendingCount };
  }, [appointments, language]);

  // Compute upcoming days & insights from real data
  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
      {/* ── Compact Action Bar ── */}
      <div className="flex-shrink-0 flex items-center gap-3 px-1 py-2">
        {/* Left: Title + action buttons */}
        <div className="flex items-center gap-3 min-w-0">
          <button 
            onClick={handleNewAppointmentClick}
            className="btn-primary !py-1.5 !px-3 !text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span>{t('newAppointment')}</span>
          </button>
          <button className="btn-secondary !py-1.5 !px-3 !text-sm">
            <ClockIcon className="h-4 w-4" />
            <span>{t('reschedule')}</span>
          </button>
          <button 
            onClick={() => navigate('/appointments/settings')}
            className="btn-secondary !py-1.5 !px-3 !text-sm"
          >
            <CogIcon className="h-4 w-4" />
            <span>Settings</span>
          </button>
        </div>

        {/* Center: Inline stats badges */}
        <div className="hidden md:flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/8 border border-primary/15">
            <CalendarIcon className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary">{stats.todayCount}</span>
            <span className="text-[10px] text-muted-foreground">{t('stats.todaysAppointments')}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/8 border border-indigo-500/15">
            <CalendarDaysIcon className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{stats.weekCount}</span>
            <span className="text-[10px] text-muted-foreground">{t('stats.thisWeek')}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/8 border border-emerald-500/15">
            <CheckCircleIcon className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{stats.confirmedCount}</span>
            <span className="text-[10px] text-muted-foreground">{t('stats.confirmed')}</span>
          </div>
          {stats.pendingCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/8 border border-amber-500/15">
              <ClockIcon className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">{stats.pendingCount}</span>
              <span className="text-[10px] text-muted-foreground">{t('stats.pending')}</span>
            </div>
          )}
        </div>

        {settingsLoaded && (
          <span className="hidden xl:inline text-[10px] text-primary/70 whitespace-nowrap">
            ✅ {workingHours.filter(h => h.is_working_day).length}j · {timeSlotConfig.minDuration}min
          </span>
        )}
      </div>

      {/* ── Main Content: Calendar + Sidebar ── */}
      <div className={`flex-1 min-h-0 grid gap-3 transition-all duration-300 ${
        isCalendarExpanded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-[1fr_320px]'
      }`}>
        {/* Calendar Section */}
        <div className="min-h-0 flex flex-col bg-card rounded-xl border border-border shadow-sm">
          {/* Calendar Toolbar - single compact row */}
          <div className="flex-shrink-0 px-3 py-1.5 border-b border-border flex items-center gap-1.5 relative z-20">
            {/* Search */}
            <div className="relative min-w-[140px] max-w-[200px] flex-shrink">
              <PatientAppointmentSearch
                initialPatient={initialPatientFromRoute}
                onPatientSelect={(_patientId, appointments, patient) => {
                  if (appointments.length > 0) {
                    const patientName = `${appointments[0].patient_name || ''}`.trim();
                    handlePatientSelect(patientName, appointments);
                    setSelectedPatient(patient);
                  } else {
                    setSelectedPatient(patient);
                  }
                }}
                onAppointmentNavigate={handleAppointmentNavigate}
                selectedPatientAppointments={selectedPatientAppointments}
                currentPatientAppointmentIndex={currentPatientAppointmentIndex}
                onClearFilter={() => {
                  setSelectedPatient(null);
                  setSelectedPatientAppointments([]);
                  setCurrentPatientAppointmentIndex(0);
                  setSelectedAppointmentId(null);
                }}
                className="border-0 shadow-none bg-transparent p-0"
              />
            </div>

            {/* Filter */}
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="bg-background border border-border rounded-lg px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/20 min-w-0"
            >
              <option value="all">{t('filters.allAppointments')}</option>
              <option value="confirmed">{t('status.confirmed')}</option>
              <option value="pending">{t('status.pending')}</option>
              <option value="emergency">{t('filters.emergency')}</option>
            </select>

            {/* View Selector */}
            <div className="flex bg-muted border border-border rounded-lg p-0.5">
              {[
                { key: 'month', label: 'Month' },
                { key: 'week', label: 'Week' },
                { key: 'day', label: 'Day' }
              ].map((view) => (
                <button
                  key={view.key}
                  onClick={() => setSelectedView(view.key)}
                  className={`px-2.5 py-0.5 text-xs font-medium rounded-md transition-all duration-200 ${
                    selectedView === view.key
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-card-foreground'
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>

            <div className="h-4 w-px bg-border flex-shrink-0" />

            {/* Display Controls */}
            <div className="flex items-center gap-1.5">
              {/* Color Mode */}
              <button
                onClick={toggleColorMode}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border transition-all ${
                  calendarColorMode === 'treatment'
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300'
                    : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                }`}
                title={calendarColorMode === 'status' 
                  ? 'Currently: Colors by Status. Click to switch to Treatment colors' 
                  : 'Currently: Colors by Treatment. Click to switch to Status colors'}
              >
                <SwatchIcon className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">
                  {calendarColorMode === 'status' ? 'Status' : 'Treatment'}
                </span>
                <span 
                  className="w-2 h-2 rounded-full border border-white/60"
                  style={{ backgroundColor: calendarColorMode === 'status' ? '#6366f1' : '#f59e0b' }}
                />
              </button>

              {/* Compact Toggle */}
              <button
                onClick={toggleHideNonWorkingHours}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border transition-all ${
                  hideNonWorkingHours
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
                    : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                }`}
                title={hideNonWorkingHours ? 'Show all hours' : 'Hide non-working hours'}
              >
                {hideNonWorkingHours ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
              </button>

              {/* Day Types */}
              <button
                onClick={() => setShowDayCategoryPanel(!showDayCategoryPanel)}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border transition-all ${
                  showDayCategoryPanel
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                }`}
                title="Set treatment category per day"
              >
                <CalendarDaysIcon className="h-3.5 w-3.5" />
              </button>

              {/* Expand/Collapse */}
              <button
                onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                className="p-1 rounded-md border border-border hover:bg-muted transition-colors"
                title={isCalendarExpanded ? 'Collapse Calendar' : 'Expand Calendar'}
              >
                {isCalendarExpanded ? (
                  <ArrowsPointingInIcon className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ArrowsPointingOutIcon className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>

          {/* Calendar Content - fills remaining space */}
          <div className="flex-1 min-h-0 overflow-auto flex flex-col px-3 py-2">
            {/* Day Category Assignment Panel */}
            {showDayCategoryPanel && (
              <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <CalendarDaysIcon className="h-4 w-4 text-primary" />
                  Day Category Assignment
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {[
                    { day: 0, label: language === 'fr' ? 'Lun' : 'Mon' },
                    { day: 1, label: language === 'fr' ? 'Mar' : 'Tue' },
                    { day: 2, label: language === 'fr' ? 'Mer' : 'Wed' },
                    { day: 3, label: language === 'fr' ? 'Jeu' : 'Thu' },
                    { day: 4, label: language === 'fr' ? 'Ven' : 'Fri' },
                    { day: 5, label: language === 'fr' ? 'Sam' : 'Sat' },
                    { day: 6, label: language === 'fr' ? 'Dim' : 'Sun' },
                  ].map(({ day, label }) => {
                    const assignment = dayCategories.find(dc => dc.day_of_week === day);
                    const isWorkingDay = workingHours.find(h => h.day_of_week === day)?.is_working_day;
                    
                    return (
                      <div key={day} className={`p-2 rounded-lg border text-center ${
                        !isWorkingDay ? 'opacity-40 border-border bg-muted/20' : 'border-border bg-card'
                      }`}>
                        <div className="text-xs font-medium text-muted-foreground mb-1.5">{label}</div>
                        <input
                          type="text"
                          placeholder="Category..."
                          className="w-full text-xs bg-background border border-border rounded px-1.5 py-1 mb-1.5 focus:outline-none focus:ring-1 focus:ring-primary/30 text-center"
                          value={assignment?.category_name || ''}
                          onChange={(e) => {
                            const newCategories = [...dayCategories];
                            const idx = newCategories.findIndex(dc => dc.day_of_week === day);
                            if (idx >= 0) {
                              newCategories[idx] = { ...newCategories[idx], category_name: e.target.value };
                            } else {
                              newCategories.push({ day_of_week: day, category_name: e.target.value, color: '#6366f1', is_active: true });
                            }
                            setDayCategories(newCategories);
                          }}
                          disabled={!isWorkingDay}
                        />
                        <input
                          type="color"
                          className="w-full h-5 rounded cursor-pointer border-0"
                          value={assignment?.color || '#6366f1'}
                          onChange={(e) => {
                            const newCategories = [...dayCategories];
                            const idx = newCategories.findIndex(dc => dc.day_of_week === day);
                            if (idx >= 0) {
                              newCategories[idx] = { ...newCategories[idx], color: e.target.value };
                            } else {
                              newCategories.push({ day_of_week: day, category_name: '', color: e.target.value, is_active: true });
                            }
                            setDayCategories(newCategories);
                          }}
                          disabled={!isWorkingDay}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={async () => {
                      for (const cat of dayCategories) {
                        await saveDayCategory(cat);
                      }
                      setShowDayCategoryPanel(false);
                    }}
                    className="px-4 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Save Day Categories
                  </button>
                </div>
              </div>
            )}

            <div className="bg-card rounded-lg border border-border overflow-hidden flex-1 min-h-0">
              {/* Dynamic CSS for FullCalendar slot height - proportional to time slot duration */}
              <style dangerouslySetInnerHTML={{
                __html: `
                  /* Target ALL possible FullCalendar slot variations with comprehensive selectors */
                  .fc-timegrid-slot,
                  .fc-timegrid-slot.fc-timegrid-slot-label,
                  .fc-timegrid-slot.fc-timegrid-slot-lane,
                  .fc-timegrid-slot.fc-timegrid-slot-minor,
                  .fc-timegrid-slot.fc-timegrid-slot-major,
                  .fc-timegrid-slot.fc-timegrid-slot-label.fc-timegrid-slot-minor,
                  .fc-timegrid-slot.fc-timegrid-slot-label.fc-timegrid-slot-major,
                  .fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-minor,
                  .fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-major {
                    height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    min-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    max-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    line-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                  }
                  
                  /* Force parent table rows to also have consistent height */
                  .fc-timegrid-slots table tr,
                  .fc-timegrid-slots tbody tr,
                  .fc-timegrid-body .fc-scrollgrid-sync-table tr {
                    height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    min-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    max-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                  }
                  
                  /* Override any FullCalendar CSS variables that might control height */
                  .fc-timegrid,
                  .fc-theme-standard .fc-timegrid {
                    --fc-timegrid-slot-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                  }
                  
                  /* Force specific height for time axis cells that contain hour labels */
                  .fc-timegrid-axis,
                  .fc-timegrid-axis-chunk,
                  .fc-timegrid-axis-chunk > table tr,
                  .fc-timegrid-axis-chunk > table td {
                    height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    min-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                    max-height: ${30 * (timeSlotConfig.minDuration / 30)}px !important;
                  }
                  
                  /* Custom event content styling */
                  .fc-event {
                    border-left-width: 3px !important;
                    border-radius: 4px !important;
                    overflow: hidden !important;
                    transition: box-shadow 0.15s ease, transform 0.1s ease !important;
                  }
                  .fc-event:hover {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
                    z-index: 10 !important;
                  }
                  .fc-event .fc-event-main {
                    padding: 0 !important;
                  }
                  
                  /* Hover effect for time slots - clickable visual feedback */
                  .fc-timegrid-slot-lane:hover,
                  .fc-timegrid-col:hover .fc-timegrid-slot-lane {
                    background-color: rgba(var(--primary), 0.08) !important;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                  }
                  
                  /* Hover effect for day grid (month view) */
                  .fc-daygrid-day:hover {
                    background-color: rgba(var(--primary), 0.08) !important;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                  }
                  
                  /* Professional highlighting for selected patient appointments */
                  .selected-patient-appointment.fc-event {
                    border: 3px solid #ef4444 !important;
                    border-left: 6px solid #ef4444 !important;
                    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.4) !important;
                    position: relative !important;
                  }
                  
                  /* Override FullCalendar's opacity reduction for selected appointments */
                  .selected-patient-appointment.fc-event {
                    opacity: 1 !important;
                  }
                  
                  /* Add a subtle glow effect for selected patient appointments */
                  .selected-patient-appointment.fc-event:hover {
                    box-shadow: 0 6px 12px rgba(239, 68, 68, 0.5) !important;
                    transform: translateY(-2px) !important;
                    transition: all 0.2s ease !important;
                  }
                  
                  /* Day header with category color - better visibility */
                  .fc-col-header-cell {
                    position: relative;
                    overflow: visible !important;
                  }
                `
              }} />
              <FullCalendar
                ref={setCalendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                customButtons={{
                  prev: {
                    click: () => handlePatientAppointmentNavigation('prev')
                  },
                  next: {
                    click: () => handlePatientAppointmentNavigation('next')
                  }
                }}
                initialView={selectedView === 'day' ? 'timeGridDay' : selectedView === 'week' ? 'timeGridWeek' : 'dayGridMonth'}
                locale={language}
                firstDay={language === 'fr' ? 1 : 0} // Monday first for French, Sunday first for English
                editable={true}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={true}
                weekends={!hideNonWorkingHours || workingHours.some(h => (h.day_of_week === 5 || h.day_of_week === 6) && h.is_working_day)}
                hiddenDays={hiddenDays}
                events={allEvents}
                businessHours={settingsLoaded ? businessHours : undefined}
                height="100%"
                slotMinTime={timeBounds.minTime}
                slotMaxTime={timeBounds.maxTime}
                slotDuration={timeSlotConfig.slotDuration}
                slotLabelInterval={timeSlotConfig.slotLabelInterval}
                snapDuration={timeSlotConfig.snapDuration}
                allDaySlot={false}
                nowIndicator={true}
                eventContent={(eventInfo) => {
                  const props = eventInfo.event.extendedProps;
                  if (props.type === 'break') return undefined; // Default FullCalendar rendering for breaks
                  
                  // Get the alternate color (the one NOT being used as main)
                  const altDotColor = calendarColorMode === 'status' 
                    ? props.treatmentColor || '#3b82f6'
                    : props.statusBorder || '#9ca3af';
                  
                  const moodInfo = MOOD_EMOJIS[props.patientMood || 'neutral'];
                  const showMood = props.patientMood && props.patientMood !== 'neutral';
                  
                  return (
                    <div className="fc-event-custom-content" style={{ 
                      width: '100%', overflow: 'hidden', padding: '1px 3px', 
                      fontSize: '0.7rem', lineHeight: '1.15', minHeight: 0 
                    }}>
                      {/* Top row: time + dots */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '1px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.65rem', opacity: 0.9 }}>
                          {eventInfo.timeText}
                        </span>
                        {/* Alternate color dot */}
                        <span
                          style={{
                            width: '7px', height: '7px', borderRadius: '50%',
                            backgroundColor: altDotColor,
                            display: 'inline-block', flexShrink: 0,
                            border: '1px solid rgba(255,255,255,0.6)',
                            boxShadow: `0 0 2px ${altDotColor}40`
                          }}
                          title={calendarColorMode === 'status' 
                            ? `Treatment: ${props.treatment || 'N/A'}` 
                            : `Status: ${(props.status || '').replace('-', ' ')}`}
                        />
                        {showMood && (
                          <span style={{ fontSize: '0.7rem', lineHeight: 1 }} title={moodInfo?.label}>
                            {moodInfo?.emoji}
                          </span>
                        )}
                      </div>
                      {/* Patient name */}
                      <div style={{ 
                        fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', 
                        textOverflow: 'ellipsis', fontSize: '0.7rem' 
                      }}>
                        {props.patient || 'No Patient'}
                      </div>
                      {/* Treatment */}
                      <div style={{ 
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        opacity: 0.8, fontSize: '0.6rem' 
                      }}>
                        {props.treatment || ''}
                      </div>
                    </div>
                  );
                }}
                selectConstraint="businessHours"
                dayHeaderContent={(arg) => {
                  // Custom day header with category label
                  const fcDay = arg.date.getDay(); // 0=Sun
                  const djangoDay = fcDay === 0 ? 6 : fcDay - 1; // Convert to Django (0=Mon)
                  const cat = dayCategories.find(dc => dc.day_of_week === djangoDay && dc.is_active && dc.category_name);
                  const dateText = arg.text; // e.g. "lun. 02/03"
                  
                  if (cat) {
                    return (
                      <div style={{ textAlign: 'center' }}>
                        <div>{dateText}</div>
                        <div style={{ 
                          fontSize: '0.6rem', color: cat.color, fontWeight: 600,
                          lineHeight: 1, marginTop: '2px',
                          borderBottom: `3px solid ${cat.color}`,
                          paddingBottom: '2px',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                          {cat.category_name}
                        </div>
                      </div>
                    );
                  }
                  return <span>{dateText}</span>;
                }}
                eventOverlap={appointmentSettings.enable_group_sessions || appointmentSettings.allow_overlapping_appointments}
                selectOverlap={(event: any) => {
                  // Don't allow selection over break periods
                  return !event.extendedProps || event.extendedProps.type !== 'break';
                }}
                eventClick={(info) => {
                  const event = info.event;
                  const props = event.extendedProps;
                  
                  if (props.type === 'break') {
                    // For break periods, just show basic info (could be enhanced with a modal later)
                    return;
                  }
                  
                  // Handle appointment selection
                  const appointmentId = props.appointmentId || event.id;
                  
                  // Check if this appointment is already selected
                  if (isAppointmentSelected(appointmentId)) {
                    // Deselect if clicking the same appointment
                    deselectAppointment();
                    return;
                  }
                  
                  // Prepare event data (same as hover)
                  const eventData = {
                    id: appointmentId,
                    patient: props.patient || 'Unknown Patient',
                    treatment: props.treatment || 'Treatment',
                    provider: props.provider || 'Provider',
                    status: props.status || 'pending',
                    startTime: event.start?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) || '',
                    endTime: event.end?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) || '',
                    date: event.start?.toLocaleDateString() || '',
                    notes: props.notes || '',
                    phone: props.phone || '',
                    patientPhone: props.patientPhone || props.phone || '',
                    patientEmail: props.patientEmail || '',
                    patientMood: props.patientMood || 'neutral',
                    treatmentColor: props.treatmentColor || '#3b82f6',
                    treatmentCategory: props.treatmentCategory || ''
                  };
                  
                  // Calculate position (same logic as hover)
                  const rect = info.el.getBoundingClientRect();
                  if (rect.width === 0 || rect.height === 0) return;
                  
                  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                  
                  const tooltipHeight = 300;
                  const tooltipWidth = 350;
                  const padding = 10;
                  
                  let x = rect.left + scrollLeft + rect.width / 2;
                  let y = rect.top + scrollTop - padding;
                  let transform = 'translateX(-50%) translateY(-100%)';
                  
                  // Check positioning adjustments
                  const wouldCutOffTop = (rect.top - tooltipHeight - padding) < 0;
                  const screenWidth = window.innerWidth;
                  const wouldCutOffLeft = (x - tooltipWidth / 2) < padding;
                  const wouldCutOffRight = (x + tooltipWidth / 2) > (screenWidth - padding);
                  
                  if (wouldCutOffTop) {
                    y = rect.top + scrollTop + rect.height + padding;
                    transform = 'translateX(-50%) translateY(0%)';
                  }
                  
                  if (wouldCutOffLeft) {
                    x = scrollLeft + padding + tooltipWidth / 2;
                  } else if (wouldCutOffRight) {
                    x = scrollLeft + screenWidth - padding - tooltipWidth / 2;
                  }
                  
                  const position = { x, y, transform };
                  
                  // Select this appointment
                  selectAppointment(eventData, position);
                }}
                dateClick={(info) => {
                  handleDateClick(info);
                }}
                select={(info) => {
                  handleDateSelect(info);
                }}
                eventDrop={(info) => {
                  // Prevent moving break periods
                  if (info.event.extendedProps?.type === 'break') {
                    info.revert();
                    return;
                  }

                  // Validate the new time slot
                  const newStart = info.event.start;
                  const newEnd = info.event.end;
                  
                  if (!newStart || !newEnd) {
                    info.revert();
                    return;
                  }

                  // Check if the new time is within business hours using actual working hours data
                  const eventDate = new Date(newStart);
                  const fcDayOfWeek = eventDate.getDay(); // 0=Sunday, 1=Monday...
                  const djangoDayOfWeek = fullCalendarToDjangoDay(fcDayOfWeek); // Convert to Django Monday=0 format
                  
                  const dayWorkingHours = workingHours.find(h => h.day_of_week === djangoDayOfWeek);
                  
                  if (!dayWorkingHours || !dayWorkingHours.is_working_day) {
                    info.revert();
                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    showToast(`Cannot move appointment to ${dayNames[fcDayOfWeek]} as it's not configured as a working day.`, 'error');
                    return;
                  }

                  // Check if appointment time falls within actual working hours
                  const eventStartTime = newStart.toTimeString().slice(0, 8); // HH:MM:SS format
                  const eventEndTime = newEnd.toTimeString().slice(0, 8);
                  
                  let timeValid = false;
                  
                  if (dayWorkingHours.has_split_shift === true || dayWorkingHours.has_split_shift === 'true') {
                    // Check if appointment falls within either morning or afternoon shift
                    const morningValid = Boolean(
                      dayWorkingHours.morning_start && dayWorkingHours.morning_end &&
                      eventStartTime >= dayWorkingHours.morning_start && 
                      eventEndTime <= dayWorkingHours.morning_end
                    );
                    const afternoonValid = Boolean(
                      dayWorkingHours.afternoon_start && dayWorkingHours.afternoon_end &&
                      eventStartTime >= dayWorkingHours.afternoon_start && 
                      eventEndTime <= dayWorkingHours.afternoon_end
                    );
                    timeValid = morningValid || afternoonValid;
                    
                    if (!timeValid) {
                      info.revert();
                      showToast(`Cannot move appointment to this time. Working hours are: ${dayWorkingHours.morning_start}-${dayWorkingHours.morning_end} and ${dayWorkingHours.afternoon_start}-${dayWorkingHours.afternoon_end}`, 'error');
                      return;
                    }
                  } else {
                    // Single shift validation
                    timeValid = Boolean(
                      dayWorkingHours.start_time && dayWorkingHours.end_time &&
                      eventStartTime >= dayWorkingHours.start_time && 
                      eventEndTime <= dayWorkingHours.end_time
                    );
                    
                    if (!timeValid) {
                      info.revert();
                      showToast(`Cannot move appointment to this time. Working hours are: ${dayWorkingHours.start_time}-${dayWorkingHours.end_time}`, 'error');
                      return;
                    }
                  }

                  // Check for break period conflicts using actual break periods data
                  const eventStartTimeStr = newStart.toTimeString().slice(0, 5); // HH:MM format for comparison
                  const eventEndTimeStr = newEnd.toTimeString().slice(0, 5);
                  
                  const conflictingBreak = breakPeriods.find(bp => 
                    bp.day_of_week === djangoDayOfWeek && 
                    ((eventStartTimeStr < bp.end_time && eventEndTimeStr > bp.start_time))
                  );
                  
                  if (conflictingBreak) {
                    info.revert();
                    showToast(`Cannot move appointment to this time as it conflicts with break period "${conflictingBreak.name}" (${conflictingBreak.start_time}-${conflictingBreak.end_time}).`, 'error');
                    return;
                  }

                  // Check group session capacity if enabled
                  if (appointmentSettings.enable_group_sessions) {
                    // Count existing appointments at the target time slot (excluding the current appointment being moved)
                    const existingAppointments = allEvents.filter(event => 
                      event.id !== info.event.id && // Exclude the appointment being moved
                      event.extendedProps?.type !== 'break' && // Exclude break periods
                      event.start && event.end &&
                      ((new Date(event.start).getTime() < newEnd.getTime()) && 
                       (new Date(event.end).getTime() > newStart.getTime())) // Check overlap
                    );
                    
                    if (existingAppointments.length >= (appointmentSettings.max_patients_per_group || 1)) {
                      info.revert();
                      showToast(`Cannot move appointment to this time slot. Maximum ${appointmentSettings.max_patients_per_group || 1} patients allowed per slot (currently ${existingAppointments.length} scheduled).`, 'error');
                      return;
                    }
                  } else if (!appointmentSettings.allow_overlapping_appointments) {
                    // If group sessions not enabled and overlapping not allowed, check for any conflicts
                    const conflictingAppointment = allEvents.find(event => 
                      event.id !== info.event.id && // Exclude the appointment being moved
                      event.extendedProps?.type !== 'break' && // Exclude break periods
                      event.start && event.end &&
                      ((new Date(event.start).getTime() < newEnd.getTime()) && 
                       (new Date(event.end).getTime() > newStart.getTime())) // Check overlap
                    );
                    
                    if (conflictingAppointment) {
                      info.revert();
                      showToast(`Time slot conflicts with existing appointment for ${conflictingAppointment.extendedProps?.patient || 'another patient'}.`, 'error');
                      return;
                    }
                  }

                  // Show confirmation dialog
                  const patientName = info.event.extendedProps?.patient || 'Unknown Patient';
                  const newDate = newStart.toLocaleDateString();
                  const newTime = `${newStart.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${newEnd.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
                  
                  const confirmed = confirm(`Do you want to move ${patientName}'s appointment to ${newDate} at ${newTime}?`);
                  
                  if (confirmed) {
                    // Update appointment in backend
                    const appointmentId = info.event.id;
                    const updateData = {
                      start_time: newStart.toISOString(),
                      end_time: newEnd.toISOString(),
                    };
                    
                    // Call the update API
                    fetch(`/api/appointments/${appointmentId}/update/`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(updateData),
                    })
                    .then(response => {
                      if (!response.ok) {
                        throw new Error('Failed to update appointment');
                      }
                      return response.json();
                    })
                    .then(_result => {
                      // Refresh appointments to get updated data
                      loadAppointments();
                    })
                    .catch(error => {
                      console.error('Error updating appointment:', error);
                      showToast('Failed to update appointment. Please try again.', 'error');
                      info.revert(); // Revert the visual change
                    });
                  } else {
                    info.revert();
                  }
                }}
                eventDragStart={(info) => {
                  // Deselect any selected appointment when starting to drag
                  if (isTooltipSelected) {
                    const draggedAppointmentId = info.event.extendedProps?.appointmentId || info.event.id;
                    // Only deselect if we're dragging the currently selected appointment
                    if (selectedEvent && selectedEvent.id === draggedAppointmentId) {
                      deselectAppointment();
                    }
                  }
                }}
                eventResize={(info) => {
                  // Prevent resizing break periods
                  if (info.event.extendedProps?.type === 'break') {
                    info.revert();
                    return;
                  }

                  // Deselect any selected appointment when starting to resize
                  if (isTooltipSelected) {
                    const resizedAppointmentId = info.event.extendedProps?.appointmentId || info.event.id;
                    // Only deselect if we're resizing the currently selected appointment
                    if (selectedEvent && selectedEvent.id === resizedAppointmentId) {
                      deselectAppointment();
                    }
                  }

                  const newEnd = info.event.end;
                  if (!newEnd) {
                    info.revert();
                    return;
                  }

                  // Validate minimum duration (based on time slot setting)
                  const duration = (newEnd.getTime() - info.event.start!.getTime()) / (1000 * 60);
                  if (duration < timeSlotConfig.minDuration) {
                    info.revert();
                    showToast(`Appointment duration must be at least ${timeSlotConfig.minDuration} minutes.`, 'error');
                    return;
                  }

                  // Show confirmation dialog
                  const patientName = info.event.extendedProps?.patient || 'Unknown Patient';
                  const newTime = `${info.event.start!.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${newEnd.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
                  
                  const confirmed = confirm(`Do you want to change ${patientName}'s appointment duration to ${newTime}?`);
                  
                  if (confirmed) {
                    // Update appointment duration in backend
                    const appointmentId = info.event.id;
                    const updateData = {
                      end_time: newEnd.toISOString(),
                    };
                    
                    // Call the update API
                    fetch(`/api/appointments/${appointmentId}/update/`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(updateData),
                    })
                    .then(response => {
                      if (!response.ok) {
                        throw new Error('Failed to update appointment');
                      }
                      return response.json();
                    })
                    .then(_result => {
                      // Refresh appointments to get updated data
                      loadAppointments();
                    })
                    .catch(error => {
                      console.error('Error updating appointment duration:', error);
                      showToast('Failed to update appointment duration. Please try again.', 'error');
                      info.revert(); // Revert the visual change
                    });
                  } else {
                    info.revert();
                  }
                }}
                eventMouseEnter={(info) => {
                  // Only show tooltip for appointment events, not break periods
                  if (info.event.extendedProps?.type !== 'break') {
                    const event = info.event;
                    const props = event.extendedProps;
                    
                    // Prepare event data
                    const hoveredEventData = {
                      id: props.appointmentId || event.id,
                      patient: props.patient || 'Unknown Patient',
                      treatment: props.treatment || 'Treatment',
                      provider: props.provider || 'Provider',
                      status: props.status || 'pending',
                      startTime: event.start?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) || '',
                      endTime: event.end?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) || '',
                      date: event.start?.toLocaleDateString() || '',
                      notes: props.notes || '',
                      phone: props.phone || '', // Deprecated - keeping for backward compatibility
                      patientPhone: props.patientPhone || props.phone || '',
                      patientEmail: props.patientEmail || '',
                      patientMood: props.patientMood || 'neutral',
                      treatmentColor: props.treatmentColor || '#3b82f6',
                      treatmentCategory: props.treatmentCategory || ''
                    };
                    
                    // Get element position for tooltip positioning (relative to document)
                    const rect = info.el.getBoundingClientRect();
                    
                    // Ensure we have valid rect dimensions
                    if (rect.width === 0 || rect.height === 0) {
                      console.warn('⚠️ Invalid element dimensions, skipping tooltip');
                      return;
                    }
                    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    
                    // Calculate smart positioning to avoid screen cutoff
                    const tooltipHeight = 300; // Estimated tooltip height
                    const tooltipWidth = 350; // Estimated tooltip width (max-w-sm is ~384px)
                    const padding = 10; // Padding from screen edges
                    
                    // Default position (above the appointment)
                    let x = rect.left + scrollLeft + rect.width / 2;
                    let y = rect.top + scrollTop - padding;
                    let transform = 'translateX(-50%) translateY(-100%)';
                    
                    // Check if tooltip would be cut off at the top
                    const wouldCutOffTop = (rect.top - tooltipHeight - padding) < 0;
                    
                    // Check if tooltip would be cut off on the left/right
                    const screenWidth = window.innerWidth;
                    const wouldCutOffLeft = (x - tooltipWidth / 2) < padding;
                    const wouldCutOffRight = (x + tooltipWidth / 2) > (screenWidth - padding);
                    
                    // Adjust vertical position if cut off at top
                    if (wouldCutOffTop) {
                      y = rect.top + scrollTop + rect.height + padding;
                      transform = 'translateX(-50%) translateY(0%)'; // Show below instead
                    }
                    
                    // Adjust horizontal position if cut off on sides
                    if (wouldCutOffLeft) {
                      x = scrollLeft + padding + tooltipWidth / 2;
                      transform = transform.replace('translateX(-50%)', 'translateX(-50%)');
                    } else if (wouldCutOffRight) {
                      x = scrollLeft + screenWidth - padding - tooltipWidth / 2;
                      transform = transform.replace('translateX(-50%)', 'translateX(-50%)');
                    }
                    
                    const position = { x, y, transform };
                    
                    // Add a small delay to ensure the DOM is stable
                    requestAnimationFrame(() => {
                      scheduleTooltipShow(hoveredEventData, position);
                    });
                  }
                }}
                eventMouseLeave={() => {
                  scheduleTooltipHide();
                }}
              />
            </div>
          </div>
        </div>

        {/* Enhanced Appointment Hover Tooltip */}
        {showTooltip && hoveredEvent && (
          <div 
            className={`appointment-tooltip absolute z-50 bg-card border rounded-lg shadow-xl p-0 max-w-sm pointer-events-auto ${
              isTooltipSelected 
                ? 'border-primary border-2 shadow-2xl shadow-primary/20' 
                : 'border-border'
            }`}
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: tooltipPosition.transform,
            }}
            onMouseEnter={() => keepTooltipVisible()}
            onMouseLeave={() => scheduleTooltipHide()}
          >
            {/* Arrow pointer - shows above or below based on tooltip position */}
            <div 
              className={`absolute left-1/2 transform -translate-x-1/2 w-0 h-0 ${
                tooltipPosition.transform.includes('translateY(-100%)') 
                  ? 'bottom-[-6px] border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-border' 
                  : 'top-[-6px] border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-border'
              }`}
            />
            <div 
              className={`absolute left-1/2 transform -translate-x-1/2 w-0 h-0 ${
                tooltipPosition.transform.includes('translateY(-100%)') 
                  ? 'bottom-[-5px] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-card' 
                  : 'top-[-5px] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-card'
              }`}
            />
            {/* Header with patient info and close button */}
            <div className={`p-4 rounded-t-lg border-b border-border ${
              isTooltipSelected ? 'bg-primary/5' : 'bg-muted/50'
            }`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-foreground">{hoveredEvent.patient}</div>
                      {isTooltipSelected && (
                        <div className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full border border-primary/20">
                          Selected
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <div className="w-2 h-2 rounded-full" style={{ 
                        backgroundColor: getStatusColors(hoveredEvent.status).bg 
                      }} />
                      <span className="capitalize text-muted-foreground">
                        {hoveredEvent.status.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => hideTooltipImmediately()}
                  className="p-1 rounded hover:bg-muted/80 transition-colors"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="p-4 space-y-3">
              <div className="grid gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Time:</span>
                  <span className="font-medium">{hoveredEvent.startTime} - {hoveredEvent.endTime}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">{hoveredEvent.date}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Treatment:</span>
                  <span className="font-medium flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hoveredEvent.treatmentColor || '#3b82f6' }} />
                    {hoveredEvent.treatment}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="font-medium">{hoveredEvent.provider}</span>
                </div>
              </div>

              {/* Contact Information */}
              {(hoveredEvent.patientPhone || hoveredEvent.patientEmail) && (
                <div className="pt-2 border-t border-border">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Contact Information</div>
                  <div className="space-y-1">
                    {hoveredEvent.patientPhone && (
                      <div className="flex items-center gap-2 text-sm">
                        <PhoneIcon className="w-4 h-4 text-muted-foreground" />
                        <span>{hoveredEvent.patientPhone}</span>
                      </div>
                    )}
                    {hoveredEvent.patientEmail && (
                      <div className="flex items-center gap-2 text-sm">
                        <EnvelopeIcon className="w-4 h-4 text-muted-foreground" />
                        <span>{hoveredEvent.patientEmail}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {hoveredEvent.notes && (
                <div className="pt-2 border-t border-border">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Notes</div>
                  <p className="text-xs text-foreground bg-muted/30 rounded p-2">{hoveredEvent.notes}</p>
                </div>
              )}

              {/* Patient Mood */}
              <div className="pt-2 border-t border-border">
                <div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center justify-between">
                  <span>Patient Mood</span>
                  {hoveredEvent.patientMood && hoveredEvent.patientMood !== 'neutral' && (
                    <span className="text-sm">{MOOD_EMOJIS[hoveredEvent.patientMood]?.emoji}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(MOOD_EMOJIS).map(([key, { emoji, label }]) => (
                    <button
                      key={key}
                      onClick={() => handleMoodUpdate(hoveredEvent.id, key)}
                      className={`text-base p-1 rounded transition-all duration-150 hover:scale-125 ${
                        hoveredEvent.patientMood === key
                          ? 'bg-primary/15 ring-1 ring-primary/40 scale-110'
                          : 'hover:bg-muted/60'
                      }`}
                      title={label}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-3 bg-muted/30 rounded-b-lg border-t border-border">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleEditAppointment(hoveredEvent.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                  title="Edit appointment"
                >
                  <PencilSquareIcon className="w-3 h-3" />
                  Edit
                </button>
                
                <button
                  onClick={() => {
                    hideTooltipImmediately();
                    navigate('/records', { state: { patientName: hoveredEvent.patient } });
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-indigo-600 dark:bg-indigo-500 text-white rounded hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                  title="Go to patient records"
                >
                  <UserIcon className="w-3 h-3" />
                  Records
                </button>
                
                <button
                  onClick={() => handleDeleteAppointment(hoveredEvent.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-destructive text-destructive-foreground rounded hover:bg-destructive/90 transition-colors"
                  title="Delete appointment"
                >
                  <TrashIcon className="w-3 h-3" />
                  Delete
                </button>

                {/* Quick Status Update - Show only for certain statuses */}
                {hoveredEvent.status === 'confirmed' && (
                  <button
                    onClick={() => handleQuickStatusUpdate(hoveredEvent.id, 'checked-in')}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 dark:bg-emerald-500 text-white rounded hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
                    title="Mark as checked in"
                  >
                    ✓ Check In
                  </button>
                )}
                
                {hoveredEvent.status === 'checked-in' && (
                  <button
                    onClick={() => handleQuickStatusUpdate(hoveredEvent.id, 'in-progress')}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    title="Mark as in progress"
                  >
                    → In Progress
                  </button>
                )}
                
                {hoveredEvent.status === 'in-progress' && (
                  <button
                    onClick={() => handleQuickStatusUpdate(hoveredEvent.id, 'completed')}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-700 dark:bg-emerald-500 text-white rounded hover:bg-emerald-800 dark:hover:bg-emerald-600 transition-colors"
                    title="Mark as completed"
                  >
                    ✓ Complete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Today's Appointments Sidebar - Only show when calendar is not expanded */}
        {!isCalendarExpanded && (
          <TodaysSchedulePanel
            appointments={schedulePanelAppointments}
            selectedDate={schedulePanelDate}
            onDateChange={setSchedulePanelDate}
            onStatusUpdate={handleAppointmentStatusUpdate}
            onNewAppointment={handleNewAppointmentClick}
            onRefresh={loadAppointments}
            onMoodUpdate={handleMoodUpdate}
            className=""
          />
        )}
      </div>

      {/* New Appointment Modal */}
      <NewAppointmentModal
        isOpen={showNewAppointmentModal}
        onClose={() => { setShowNewAppointmentModal(false); setEditingAppointmentId(null); }}
        onSave={handleCreateAppointment}
        initialDate={selectedDate}
        initialTime={selectedTime}
        initialDuration={selectedDuration}
        preselectedPatient={selectedPatient}
      />

      {/* Themed Confirm Dialog */}
      <ConfirmDialog
        open={dialogState.open}
        title={dialogState.title}
        message={dialogState.message}
        variant={dialogState.variant}
        confirmLabel={dialogState.confirmLabel}
        onConfirm={dialogState.onConfirm || (() => setDialogState(prev => ({ ...prev, open: false })))}
        onCancel={dialogState.onCancel || (() => setDialogState(prev => ({ ...prev, open: false })))}
      />

      {/* Toast Notification */}
      {toastState.show && (
        <div className="fixed bottom-6 right-6 z-[10001] animate-[slideUp_300ms_ease-out]">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border backdrop-blur-sm max-w-sm ${
            toastState.type === 'error' 
              ? 'bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200' 
              : toastState.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
              : 'bg-blue-50 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
          }`}>
            <span className="text-sm font-medium">{toastState.message}</span>
            <button
              onClick={() => setToastState(prev => ({ ...prev, show: false }))}
              className="ml-2 text-current opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Export memoized component for better performance
export default memo(AppointmentsClientWithSettings);
